/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import axios from 'axios';

// Define interfaces for the data structure
interface TableRowData {
  cycle: string;
  eol: boolean;
  latest: string;
  latestReleaseDate: string;
  lts: boolean;
  releaseDate: string;
}

// Fetch data function
export async function fetchData(serviceType: string): Promise<TableRowData[]> {
  try {
    const response = await fetch(`https://endoflife.date/api/${serviceType}.json`);

    if (!response.ok) {
      throw new Error(`Failed to fetch data. HTTP Status: ${response.status}`);
    }

    const data: TableRowData[] = await response.json();
    return data.slice(0, 5); // Fetch only the top 5 rows
  } catch (error) {
    console.error(`Error fetching data for ${serviceType}:`, error);
    throw error; // Ensure the error is propagated
  }
}

// Fetch latest version from GitHub API
export async function fetchLatestVersionFromGitHubApi(repoUrl: string, serviceType: string) {
  try {
    // Extract the owner and repo name from the GitHub URL
    const match = repoUrl.match(/github\.com\/([^/]+)\/([^/]+)/);
    if (!match) {
      throw new Error('Invalid GitHub URL');
    }
    const owner = match[1];
    const repo = match[2];

    // GitHub API endpoint for tags
    const apiUrl = `https://api.github.com/repos/${owner}/${repo}/tags`;

    // Make the API request
    const response = await axios.get(apiUrl, {
      headers: {
        // Add your GitHub personal access token here if you encounter rate limits
        // 'Authorization': 'Bearer YOUR_PERSONAL_ACCESS_TOKEN'
      },
    });

    if (response.status === 200 && response.data.length > 0) {
      const tags = response.data.slice(0, 5); // Fetch only the top 5 tags

      // Fetch the date for each tag
      const tagsWithDates = await Promise.all(
        tags.map(async (tag: any) => {
          const commitSha = tag.commit.sha;

          // Fetch commit details to get the date
          const commitResponse = await axios.get(
            `https://api.github.com/repos/${owner}/${repo}/commits/${commitSha}`,
            {
              headers: {},
            },
          );

          const commitDate = commitResponse.data.commit.author.date;

          return {
            tag_name: tag.name,
            date: new Date(commitDate).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }),
          };
        }),
      );

      return tagsWithDates;
    } else {
      console.error(`No tags found for repository: ${repoUrl}`);
      return [];
    }
  } catch (error: any) {
    console.error(`Error fetching data from GitHub API for ${serviceType}: ${error.message}`);
    throw error; // Ensure the error is propagated
  }
}

// Fetch end-of-life version
export async function fetchEndOfLifeVersion(toolName: string) {
  const urlsAndTypes = [
    { url: 'https://endoflife.date/nginx', name: 'nginx' },
    { url: 'https://endoflife.date/argo-cd', name: 'argo-cd' },
    { url: 'https://endoflife.date/azure-kubernetes-service', name: 'aks' },
    { url: 'https://endoflife.date/redis', name: 'redis' },
    { url: 'https://endoflife.date/postgresql', name: 'postgres' },
    { url: 'https://endoflife.date/mssqlserver', name: 'mssqlserver' },
    { url: 'https://github.com/pgadmin-org/pgadmin4/tags', name: 'pgadmin' },
    { url: 'https://github.com/DandyDeveloper/charts/tags', name: 'redis-ha' },
    {
      url: 'https://github.com/kubernetes/ingress-nginx/tags',
      name: 'ingress-nginx',
    },
    {
      url: 'https://github.com/RedisInsight/RedisInsight/tags',
      name: 'redisinsight',
    },
  ];

  const tool = urlsAndTypes.find(t => t.name === toolName);
  if (!tool) {
    return null;
  }

  try {
    if (tool.url.includes('github')) {
      return await fetchLatestVersionFromGitHubApi(tool.url, tool.name);
    } else {
      return await fetchData(tool.name);
    }
  } catch (error) {
    console.error('Error fetching end-of-life version:', error);
    return null; // Return null on error
  }
}